import { Preferences } from '@capacitor/preferences'
import { Capacitor } from '@capacitor/core'
import { SecureStorage } from '@aparajita/capacitor-secure-storage'
import * as Sentry from '@sentry/vue'

const SECURE_KEYS_STORAGE_KEY = '__secure_storage_keys__'
let secureKeyCache = null
let secureKeyCachePromise = null

// The Keychain (iOS) and the Keystore (Android), through @aparajita/capacitor-secure-storage:
// string values, a null answer for a missing key.
const getSecurePlugin = () => (Capacitor.isPluginAvailable('SecureStorage') ? SecureStorage : null)

const readSecureValue = (plugin, key) => plugin.getItem(key)

const writeSecureValue = async (plugin, key, value) => {
    await plugin.setItem(key, value)
    return true
}

const removeSecureValue = async (plugin, key) => {
    await plugin.removeItem(key)
    return true
}

const persistSecureKeyCache = async () => {
    if (!secureKeyCache) return
    try {
        if (secureKeyCache.size === 0) {
            await Preferences.remove({ key: SECURE_KEYS_STORAGE_KEY })
            return
        }
        await Preferences.set({
            key: SECURE_KEYS_STORAGE_KEY,
            value: JSON.stringify(Array.from(secureKeyCache)),
        })
    } catch (err) {
        Sentry.captureMessage('Secure storage key cache save failed')
    }
}

const loadSecureKeyCache = async (plugin) => {
    if (secureKeyCache) return secureKeyCache
    if (secureKeyCachePromise) return secureKeyCachePromise

    secureKeyCachePromise = (async () => {
        let keys = null
        try {
            const stored = await Preferences.get({ key: SECURE_KEYS_STORAGE_KEY })
            if (stored?.value) {
                const parsed = JSON.parse(stored.value)
                if (Array.isArray(parsed)) {
                    keys = parsed
                }
            }
        } catch (err) {
            Sentry.captureMessage('Secure storage key cache load failed')
        }

        if ((!keys || keys.length === 0) && plugin && typeof plugin.keys === 'function') {
            try {
                const result = await plugin.keys()
                // The plugin answers a bare array, whose own `keys` is Array.prototype.keys.
                const rawKeys = Array.isArray(result) ? result : (result?.value ?? result?.keys)
                if (Array.isArray(rawKeys)) {
                    keys = rawKeys
                }
            } catch (err) {
                secureKeyCachePromise = null
                throw err
            }
        }

        secureKeyCache = new Set(Array.isArray(keys) ? keys : [])
        secureKeyCachePromise = null
        if (secureKeyCache.size > 0) {
            await persistSecureKeyCache()
        }
        return secureKeyCache
    })()

    return secureKeyCachePromise
}

const updateSecureKeyCache = async (plugin, key, exists) => {
    if (!plugin) return
    try {
        const cache = await loadSecureKeyCache(plugin)
        if (!cache) return
        if (exists) {
            cache.add(key)
        } else {
            cache.delete(key)
        }
        await persistSecureKeyCache()
    } catch (err) {
        // ignore cache update errors to avoid blocking main flow
    }
}

// A value the Keystore or the Keychain cannot read (a key reset, a backup restored on another
// phone) fails the same way on every call: reported once per key and operation this session.
const reportedStorageFailures = new Set()

const reportStorageFailureOnce = (operation, key, error) => {
    const id = `${operation}:${key}`
    if (reportedStorageFailures.has(id)) return
    reportedStorageFailures.add(id)
    Sentry.captureMessage(`${operation} failed for ${key}`, {
        level: 'warning',
        extra: { error: String(error?.message || error || '') },
    })
}

const isMissingSecureItem = (error) => {
    const message = typeof error?.message === 'string' ? error.message.toLowerCase() : ''
    return message.includes('does not exist') || message.includes('not found') || message.includes('missing')
}

/**
 * Memory class is used for
 * setting or getting data from
 * the phone's memory.
 */
export default class Memory {
    /**
     * Clear keys and values from storage.
     *
     */
    static async clear() {
        return await Preferences.clear()
    }
    /**
     * Return the list of known keys in storage.
     *
     */
    static async keys() {
        return (await Preferences.keys())?.keys
    }

    /**
     * Gets an item from the phone's
     * memory. This uses Capacitor's
     * Preferences API.
     */
    static async get(key, { decode = true, otherwise = undefined } = {}) {
        let value
        try {
            value = (await Preferences.get({ key: key })).value
        } catch (err) {
            reportStorageFailureOnce('Preferences read', key, err)
            return otherwise
        }
        if (value && value !== 'undefined' && decode) {
            try {
                value = JSON.parse(value)
            } catch (err) {
                Sentry.captureMessage('Impossible to parse JSON for ' + key)
            }
        }
        return value ?? otherwise
    }

    /**
     * Sets an item to the phone's
     * memory. This uses Capacitor's
     * Preferences API.
     */
    static async set(key, value, { encode = true } = {}) {
        if (value && value !== 'undefined' && encode) {
            try {
                value = JSON.stringify(value)
            } catch (err) {
                Sentry.captureMessage('Impossible to stringify JSON for ' + key)
            }
        }
        await Preferences.set({ key, value })
    }

    /**
     * Gets an item from secure storage if available.
     */
    static async getSecure(key, { decode = true, otherwise = undefined } = {}) {
        const plugin = getSecurePlugin()
        let value = undefined

        if (plugin) {
            let shouldRead = true
            try {
                const cache = await loadSecureKeyCache(plugin)
                if (cache && !cache.has(key)) {
                    shouldRead = false
                }
            } catch (err) {
                shouldRead = true
            }

            try {
                if (shouldRead) {
                    value = await readSecureValue(plugin, key)
                }
            } catch (err) {
                if (isMissingSecureItem(err)) {
                    await updateSecureKeyCache(plugin, key, false)
                } else {
                    // Unreadable is missing: the caller falls back as if nothing were stored.
                    reportStorageFailureOnce('Secure storage read', key, err)
                }
            }
        }

        if (value === undefined || value === null) {
            return this.get(key, { decode, otherwise })
        }

        if (value && value !== 'undefined' && decode) {
            try {
                value = JSON.parse(value)
            } catch (err) {
                Sentry.captureMessage('Impossible to parse JSON for ' + key)
            }
        }
        return value ?? otherwise
    }

    /**
     * Sets an item to secure storage if available.
     */
    static async setSecure(key, value, { encode = true } = {}) {
        const plugin = getSecurePlugin()
        let rawValue = value

        if (value && value !== 'undefined' && encode) {
            try {
                rawValue = JSON.stringify(value)
            } catch (err) {
                Sentry.captureMessage('Impossible to stringify JSON for ' + key)
            }
        }

        if (plugin) {
            try {
                const stored = await writeSecureValue(plugin, key, rawValue)
                if (stored) {
                    await updateSecureKeyCache(plugin, key, true)
                    return true
                }
            } catch (err) {
                reportStorageFailureOnce('Secure storage write', key, err)
            }
        }

        await this.set(key, value, { encode })
        return false
    }

    /**
     * Removes a key / value pair from secure storage if available.
     */
    static async removeSecure(key) {
        const plugin = getSecurePlugin()

        if (plugin) {
            try {
                const removed = await removeSecureValue(plugin, key)
                if (removed) {
                    await updateSecureKeyCache(plugin, key, false)
                    return
                }
            } catch (err) {
                reportStorageFailureOnce('Secure storage remove', key, err)
            }
        }

        return this.remove(key)
    }

    /**
     * Indicates if a secure storage plugin is available.
     */
    static isSecureAvailable() {
        return Boolean(getSecurePlugin())
    }

    /**
     * Removes a key / value pair from the
     * phone's memory.
     */
    static remove(key) {
        return Preferences.remove({ key })
    }

    /**
     * Gets an item from the phone's
     * memory if it exists and equals
     * the given value or sets a new
     * value on the phone's memory and
     * returns the new one.
     */
    static async sear(key, value, { enAndDecode = true } = {}) {
        const found = this.get(key, { decode: enAndDecode })

        if (value && found !== value) {
            await this.set(key, value, { encode: enAndDecode })
            return value
        }

        return found
    }
}
